import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'

export interface LibraryStatusData {
  type: 'physical' | 'digital'
  typeName: string
  read: number
  reading: number
  unread: number
  total: number
  readPercentage: number
}

export interface UseLibraryStatusReturn {
  data: LibraryStatusData[]
  isLoading: boolean
  error: Error | null
}

/**
 * Hook for library status data (physical vs digital breakdown by status)
 * Uses a dedicated SQL function for accurate counts
 */
export function useLibraryStatus(): UseLibraryStatusReturn {
  const { t } = useTranslation()
  const { user } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['library-status-by-type', user?.id],
    queryFn: async (): Promise<LibraryStatusData[]> => {
      if (!user?.id) throw new Error('User not authenticated')

      const { data: statusData, error } = await supabase.rpc('get_library_status_by_type', {
        user_uuid: user.id,
      })

      if (error) {
        console.error('Library status error:', error)
        throw new Error(`Failed to fetch library status: ${error.message}`)
      }

      // Transform the data from the database
      const result: LibraryStatusData[] = []

      statusData?.forEach(
        (row: {
          book_type: string
          read_count: number
          reading_count: number
          unread_count: number
          total_count: number
        }) => {
          if (row.book_type === 'all') return // Skip the 'all' row

          const type = row.book_type as 'physical' | 'digital'
          result.push({
            type,
            typeName: t(`books.type.${type}`),
            read: row.read_count || 0,
            reading: row.reading_count || 0,
            unread: row.unread_count || 0,
            total: row.total_count || 0,
            readPercentage:
              row.total_count > 0 ? Math.round((row.read_count / row.total_count) * 100) : 0,
          })
        }
      )

      return result.filter((item) => item.total > 0) // Remove empty categories
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  })

  return {
    data: data || [],
    isLoading,
    error: error as Error | null,
  }
}
